import { useEffect, useRef, useState } from "react";
import type { HandInput, RemoteInput } from "@picnic-field/local-host/protocol";
import { useI18n } from "../../../i18n";
import type { StageInput } from "../lib/stage";

/**
 * 기구 무대 '센서 손' 패널(2026-10-06) — 아이가 화면에서 허브 · 센서를 직접 다룬다(Bench 시뮬레이터 센서 판과 같은 생각).
 * 정답 프로그램이 읽는 입력만 보인다(무대 설정 stage.inputs): 허브 ◀ ▶(리모컨 A · B — 누르고 있는 동안) · 허브 기울이기(앞뒤 · 좌우) ·
 * 힘 센서 꾹 누르기(누르는 동안) · 색 카드 · 손 거리(손 대기 + 거리). 바뀔 때만 호스트로 보낸다(커널이 틱 경계에서 적용 · 리플레이에 기록).
 */
export const HAND_IDLE: HandInput = { pitch: 0, roll: 0, force: 0, color: -1, distance: -1 };

/** SPIKE 색 번호(색 센서) — 악기 정답: 빨 9 · 주 8 · 노 7 · 초 6 · 파 3 */
const COLORS: readonly { id: number; key: string; css: string }[] = [
  { id: 9, key: "red", css: "#E8414F" },
  { id: 8, key: "orange", css: "#FF9F1C" },
  { id: 7, key: "yellow", css: "#FFD23F" },
  { id: 6, key: "green", css: "#3DBE5A" },
  { id: 3, key: "blue", css: "#2E7BD1" },
  { id: 1, key: "magenta", css: "#C2409B" },
  { id: 10, key: "white", css: "#FFFFFF" },
];

export function HandPanel({
  inputs,
  disabled,
  onHand,
  onButtons,
}: {
  inputs: readonly StageInput[];
  disabled: boolean;
  onHand: (h: HandInput) => void;
  onButtons: (r: RemoteInput) => void;
}) {
  const { t } = useI18n();
  const [hand, setHand] = useState<HandInput>(HAND_IDLE);
  // 손을 댄 거리(mm — 허브 거리 센서와 같은 단위 · 2026-10-07). 40mm 면 차시 정답의 '< 50' · '< 100' 이 모두 켜진다
  const [near, setNear] = useState(40);
  const [btn, setBtn] = useState<{ a: boolean; b: boolean }>({ a: false, b: false });
  const sent = useRef<HandInput>(HAND_IDLE);

  useEffect(() => {
    const p = sent.current;
    if (
      p.pitch === hand.pitch &&
      p.roll === hand.roll &&
      p.force === hand.force &&
      p.color === hand.color &&
      p.distance === hand.distance
    )
      return;
    sent.current = hand;
    onHand(hand);
  }, [hand, onHand]);
  useEffect(() => {
    onButtons({ dpad: null, a: btn.a, b: btn.b, slider: 0 });
  }, [btn, onButtons]);
  // 판이 멈추면 손을 뗀다(다음 판은 처음부터)
  useEffect(() => {
    if (disabled) {
      setHand(HAND_IDLE);
      setBtn({ a: false, b: false });
    }
  }, [disabled]);

  const has = (k: StageInput) => inputs.includes(k);
  const set = (p: Partial<HandInput>) => setHand((h) => ({ ...h, ...p }));
  /** 누르는 동안만 켜지는 단추(손가락 · 마우스 · 키보드 스페이스) */
  const hold = (on: () => void, off: () => void) => ({
    onPointerDown: (e: React.PointerEvent) => {
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      on();
    },
    onPointerUp: off,
    onPointerCancel: off,
    onPointerLeave: off,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === " " || e.key === "Enter") on();
    },
    onKeyUp: off,
    disabled,
  });

  if (!inputs.length) return null;
  return (
    <section className="pf-hand" aria-label={t("stage.hand.title")} data-testid="stage-hand">
      <p className="pf-hand__title">🤚 {t("stage.hand.title")}</p>
      {has("buttons") && (
        <div className="pf-hand__row" role="group" aria-label={t("stage.hand.buttons")}>
          <button
            type="button"
            className={`pf-hand__btn${btn.a ? " is-on" : ""}`}
            data-testid="hand-left"
            {...hold(
              () => setBtn((b) => ({ ...b, a: true })),
              () => setBtn((b) => ({ ...b, a: false })),
            )}
          >
            ◀ {t("stage.hand.left")}
          </button>
          <button
            type="button"
            className={`pf-hand__btn${btn.b ? " is-on" : ""}`}
            data-testid="hand-right"
            {...hold(
              () => setBtn((b) => ({ ...b, b: true })),
              () => setBtn((b) => ({ ...b, b: false })),
            )}
          >
            {t("stage.hand.right")} ▶
          </button>
        </div>
      )}
      {has("pitch") && (
        <label className="pf-hand__slider">
          <span>
            ↕ {t("stage.hand.pitch")} <b>{hand.pitch}°</b>
          </span>
          <input
            type="range"
            min={-45}
            max={45}
            step={1}
            value={hand.pitch}
            disabled={disabled}
            data-testid="hand-pitch"
            onChange={(e) => set({ pitch: Number(e.target.value) })}
            onDoubleClick={() => set({ pitch: 0 })}
          />
        </label>
      )}
      {has("roll") && (
        <label className="pf-hand__slider">
          <span>
            ↔ {t("stage.hand.roll")} <b>{hand.roll}°</b>
          </span>
          <input
            type="range"
            min={-45}
            max={45}
            step={1}
            value={hand.roll}
            disabled={disabled}
            data-testid="hand-roll"
            onChange={(e) => set({ roll: Number(e.target.value) })}
            onDoubleClick={() => set({ roll: 0 })}
          />
        </label>
      )}
      {has("color") && (
        <div
          className="pf-hand__row pf-hand__colors"
          role="radiogroup"
          aria-label={t("stage.hand.color")}
        >
          <span className="pf-hand__label">🎨 {t("stage.hand.color")}</span>
          {COLORS.map((c) => (
            <button
              key={c.id}
              type="button"
              role="radio"
              aria-checked={hand.color === c.id}
              aria-label={t(`stage.hand.colors.${c.key}`)}
              className={`pf-hand__chip${hand.color === c.id ? " is-on" : ""}`}
              style={{ background: c.css }}
              disabled={disabled}
              data-testid={`hand-color-${c.id}`}
              onClick={() => set({ color: hand.color === c.id ? -1 : c.id })}
            />
          ))}
        </div>
      )}
      {has("force") && (
        <button
          type="button"
          className={`pf-hand__btn pf-hand__btn--wide${hand.force > 0 ? " is-on" : ""}`}
          data-testid="hand-force"
          {...hold(
            () => set({ force: 60 }),
            () => set({ force: 0 }),
          )}
        >
          👉 {t("stage.hand.force")}
        </button>
      )}
      {has("distance") && (
        <div className="pf-hand__row">
          <button
            type="button"
            className={`pf-hand__btn${hand.distance >= 0 ? " is-on" : ""}`}
            data-testid="hand-near"
            disabled={disabled}
            onClick={() => set({ distance: hand.distance >= 0 ? -1 : near })}
          >
            ✋ {t(hand.distance >= 0 ? "stage.hand.away" : "stage.hand.near")}
          </button>
          <label className="pf-hand__slider pf-hand__slider--grow">
            <span>
              {t("stage.hand.distance")} <b>{near}mm</b>
            </span>
            <input
              type="range"
              min={10}
              max={2000}
              step={10}
              value={near}
              disabled={disabled}
              data-testid="hand-distance"
              onChange={(e) => {
                const v = Number(e.target.value);
                setNear(v);
                if (hand.distance >= 0) set({ distance: v });
              }}
            />
          </label>
        </div>
      )}
    </section>
  );
}
